/**
 * GRIDHEIMR — Create Bot Page
 *
 * Placeholder form for creating a new bot instance.
 * Wires into the existing createBot API when the backend is ready.
 */

import React, { useState, FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createBot } from "../api/bots";
import { BotType } from "../api/types";
import { BLUE, YELLOW, PIXEL, MONO, FONT_URL } from "../theme";

const BOT_TYPES = [
  { value: BotType.MOLTBOT,  label: "MoltBot",  icon: "\u{1F916}" },
  { value: BotType.OPENCLAW, label: "OpenClaw", icon: "\u{1F99E}" },
  { value: BotType.PICOCLAW, label: "PicoClaw", icon: "\u{1F980}" },
  { value: BotType.PICOBOT,  label: "PicoBot",  icon: "\u{1F41E}" },
  { value: BotType.NANOBOT,  label: "NanoBot",  icon: "\u{1F9EC}" },
  { value: BotType.CUSTOM,   label: "Custom",   icon: "\u{1F527}" },
];

export function CreateBotPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [botType, setBotTypeRaw] = useState<BotType>(BotType.CUSTOM);
  const [vcpus, setVcpus] = useState(1);
  const [memoryMb, setMemoryMb] = useState(512);

  // When selecting picoclaw or picobot, enforce minimum resources for Chromium sidecar
  const setBotType = (type: BotType) => {
    setBotTypeRaw(type);
    if (type === BotType.PICOCLAW || type === BotType.PICOBOT) {
      if (vcpus < 2) setVcpus(2);
      if (memoryMb < 768) setMemoryMb(1024);
    }
  };
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!document.getElementById("create-bot-fonts")) {
      const l = Object.assign(document.createElement("link"), {
        id: "create-bot-fonts", rel: "stylesheet", href: FONT_URL,
      });
      document.head.appendChild(l);
    }
    if (!document.getElementById("create-bot-css")) {
      const s = Object.assign(document.createElement("style"), { id: "create-bot-css" });
      s.textContent = `
        .gh-cb-input:focus {
          border-color: ${BLUE} !important;
          box-shadow: 0 0 12px rgba(0,106,167,0.4) !important;
          outline: none;
        }
        .gh-cb-submit:hover:not(:disabled) {
          background: ${YELLOW} !important;
          color: #000 !important;
          box-shadow: 0 0 20px rgba(254,204,0,0.4) !important;
        }
        .gh-cb-cancel:hover { border-color: ${YELLOW} !important; color: ${YELLOW} !important; }
        .gh-cb-type-btn.active {
          border-color: ${YELLOW} !important;
          color: ${YELLOW} !important;
          box-shadow: 0 0 10px rgba(254,204,0,0.2) !important;
        }
        .gh-cb-type-btn:hover { border-color: ${BLUE} !important; color: #fff !important; }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Bot name is required."); return; }
    setError(null);
    setIsLoading(true);
    try {
      const bot = await createBot({
        name: name.trim(),
        bot_type: botType,
        resources: { vcpus, memory_mb: memoryMb },
      });
      navigate(`/bots/${bot.id}`);
    } catch (err: any) {
      setError(err.message || "Failed to create bot. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={S.container}>
      <p style={S.sectionLabel}>{"// NEW BOT"}</p>
      <h1 style={S.title}>Deploy a New Agent</h1>

      <form onSubmit={handleSubmit} style={S.form}>
        {error && (
          <div style={S.errorBox}><span style={S.errorText}>{error}</span></div>
        )}

        {/* Bot Name */}
        <div style={S.field}>
          <label style={S.label}>BOT NAME</label>
          <input
            className="gh-cb-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={isLoading}
            style={S.input}
            placeholder="my-agent-01"
            maxLength={64}
          />
        </div>

        {/* Bot Type */}
        <div style={S.field}>
          <label style={S.label}>TYPE</label>
          <div style={S.typeGrid}>
            {BOT_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`gh-cb-type-btn${botType === t.value ? " active" : ""}`}
                style={S.typeBtn}
                onClick={() => setBotType(t.value)}
                disabled={isLoading}>
                <span style={{ fontSize: "1.2rem" }}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Resources */}
        <div style={S.field}>
          <label style={S.label}>RESOURCES</label>
          <div style={S.resourceRow}>
            <div style={S.resourceItem}>
              <span style={S.resourceLabel}>vCPUs</span>
              <select
                className="gh-cb-input"
                value={vcpus}
                onChange={(e) => setVcpus(Number(e.target.value))}
                disabled={isLoading}
                style={S.select}>
                {[1, 2, 4].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={S.resourceItem}>
              <span style={S.resourceLabel}>Memory</span>
              <select
                className="gh-cb-input"
                value={memoryMb}
                onChange={(e) => setMemoryMb(Number(e.target.value))}
                disabled={isLoading}
                style={S.select}>
                {[256, 512, 1024, 2048, 4096].map((m) => (
                  <option key={m} value={m}>{m >= 1024 ? `${m / 1024} GB` : `${m} MB`}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Actions */}
        <div style={S.actions}>
          <button
            type="submit"
            disabled={isLoading}
            className="gh-cb-submit"
            style={S.submitBtn}>
            {isLoading ? "DEPLOYING..." : "⚡ DEPLOY BOT"}
          </button>
          <button
            type="button"
            className="gh-cb-cancel"
            style={S.cancelBtn}
            onClick={() => navigate("/dashboard")}
            disabled={isLoading}>
            CANCEL
          </button>
        </div>
      </form>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  container: { width: "100%" },
  sectionLabel: {
    fontFamily: MONO, fontSize: "0.7rem", color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.15em", margin: "0 0 0.75rem 0",
  },
  title: {
    fontFamily: PIXEL, fontSize: "clamp(0.8rem, 2.5vw, 1.2rem)", fontWeight: 400,
    margin: "0 0 2rem 0", color: "#fff", letterSpacing: "0.06em",
    textShadow: `0 0 15px rgba(0,106,167,0.4)`,
  },
  form: {
    display: "flex", flexDirection: "column", gap: "1.75rem",
    maxWidth: "600px",
  },
  errorBox: {
    background: "rgba(255,68,68,0.12)", border: "1px solid rgba(255,68,68,0.4)",
    padding: "0.75rem 1rem",
  },
  errorText: { fontFamily: MONO, fontSize: "0.8rem", color: "#ff6b6b" },
  field: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  label: {
    fontFamily: PIXEL, fontSize: "0.4rem", color: YELLOW,
    letterSpacing: "0.15em", textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  input: {
    fontFamily: MONO, fontSize: "0.9rem", color: "#fff",
    background: "rgba(0,106,167,0.06)", border: `1px solid rgba(0,106,167,0.25)`,
    padding: "12px 14px", transition: "all 0.2s ease", outline: "none",
  },
  typeGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "8px",
  },
  typeBtn: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: "0.4rem",
    padding: "1rem 0.75rem", background: "rgba(0,106,167,0.06)",
    border: `1px solid rgba(0,106,167,0.2)`, cursor: "pointer",
    fontFamily: PIXEL, fontSize: "0.35rem", color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.08em", transition: "all 0.2s ease",
  },
  resourceRow: { display: "flex", gap: "1rem" },
  resourceItem: { display: "flex", flexDirection: "column", gap: "0.3rem", flex: 1 },
  resourceLabel: {
    fontFamily: MONO, fontSize: "0.7rem", color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.08em",
  },
  select: {
    fontFamily: MONO, fontSize: "0.85rem", color: "#fff",
    background: "rgba(0,106,167,0.06)", border: `1px solid rgba(0,106,167,0.25)`,
    padding: "10px 14px", outline: "none", cursor: "pointer",
    transition: "all 0.2s ease",
  },
  actions: { display: "flex", gap: "0.75rem", paddingTop: "0.5rem" },
  submitBtn: {
    fontFamily: PIXEL, fontSize: "0.45rem", fontWeight: 400,
    padding: "14px 28px", background: "transparent", color: YELLOW,
    border: `2px solid ${YELLOW}`, cursor: "pointer", letterSpacing: "0.12em",
    transition: "all 0.2s ease", boxShadow: `0 0 10px rgba(254,204,0,0.2)`,
  },
  cancelBtn: {
    fontFamily: PIXEL, fontSize: "0.4rem", fontWeight: 400,
    padding: "14px 20px", background: "transparent",
    color: "rgba(255,255,255,0.4)", border: `1px solid rgba(255,255,255,0.15)`,
    cursor: "pointer", letterSpacing: "0.1em", transition: "all 0.2s ease",
  },
};
